import type { ContourId } from '@/src/store/contours/contour-definitions';

import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import {
  ContourLabel,
  encodeContourLabelItemId,
} from '@/src/store/contours/contour-definitions';
import {
  convertPointArrayToArray,
  convertPointSiArrayToArray,
} from '@/src/store/contours/contour-processing/convertArrayToPointArray';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { Orientation } from '@/src/store/dicom-web/classification-typing';

interface ExportPoint { x: number; y: number; si: number }

export interface ContourExport {
  name: string;
  points: ExportPoint[];
}

const laxOrientations = new Set<Orientation>([
  Orientation.Lax2CV,
  Orientation.Lax3CV,
  Orientation.Lax4CV,
  Orientation.Lax5CV,
]);

function isLaxOrientation(orientation: Orientation) {
  return laxOrientations.has(orientation);
}

export function useMaskExtractor() {
  const contourContainerStore = useContourContainerStore();
  const viewportStateStore = useViewportStateStore();

  // ─── Contour ID Resolution ──────────────────────────────────────────
  /**
   * Resolves the correct contour IDs for myocardium mask, endo, and epi
   * based on orientation and viewport transmurality offsets.
   */
  function getContourIdsForOrientation(
    orientation: Orientation,
    viewerID: string,
  ) {
    const endoOffset
      = viewportStateStore.getSaxLvTransmuralityOffsetEndo(viewerID);
    const epiOffset
      = viewportStateStore.getSaxLvTransmuralityOffsetEpi(viewerID);

    if (isLaxOrientation(orientation)) {
      return {
        myoContourId: {
          label: ContourLabel.LaxLvMyoFusion,
          id: encodeContourLabelItemId([endoOffset, 100 - epiOffset]),
        },
        endoContourId: {
          label: ContourLabel.LaxLvEndo,
          id: encodeContourLabelItemId([0]),
        },
        epiContourId: {
          label: ContourLabel.LaxLvEpi,
          id: encodeContourLabelItemId([0]),
        },
      };
    }

    return {
      myoContourId: {
        label: ContourLabel.SaxLvMyoFusion,
        id: encodeContourLabelItemId([endoOffset, 100 - epiOffset]),
      },
      endoContourId: {
        label: ContourLabel.SaxLvEndo,
        id: encodeContourLabelItemId([0]),
      },
      epiContourId: {
        label: ContourLabel.SaxLvEpi,
        id: encodeContourLabelItemId([0]),
      },
    };
  }

  // ─── Segmentation Geometry (for analysis payload) ───────────────────
  /**
   * Retrieves mask/polyline geometry for a given image using proper
   * transmurality offsets. Returns coordinate arrays for the analysis
   * backend payload.
   *
   * Tries the myocardium mask first; if it exists, returns that alone.
   * Otherwise falls back to raw endo/epi polylines.
   */
  function getImageSegmentationGeometry(
    imageUID: string,
    orientation: Orientation,
    viewerID: string,
  ) {
    const container = contourContainerStore.contourContainers[imageUID];
    if (!container) {
      return {
        myoMask: [] as [number, number][],
        endoContour: [] as [number, number][],
        epiContour: [] as [number, number][],
      };
    }

    const { myoContourId, endoContourId, epiContourId }
      = getContourIdsForOrientation(orientation, viewerID);

    let myoMask: [number, number][] = [];
    try {
      myoMask = convertPointSiArrayToArray(
        container.getContourMaskCompleteIn(myoContourId),
      ) as [number, number][];
    }
    catch (error) {
      myoMask = [] as [number, number][];
      console.warn(`Failed to extract myocardium mask for image ${imageUID}:`, error);
    }

    if (myoMask.length > 0) {
      return {
        myoMask,
        endoContour: [] as [number, number][],
        epiContour: [] as [number, number][],
      };
    }

    const endoContour = convertPointArrayToArray(
      container.getContourPointArray(endoContourId),
    ) as [number, number][];
    const epiContour = convertPointArrayToArray(
      container.getContourPointArray(epiContourId),
    ) as [number, number][];

    return {
      myoMask,
      endoContour,
      epiContour,
    };
  }

  // ─── Mask Pixel Export (for image extraction / canvas drawing) ──────
  /**
   * Retrieves mask pixel data ({x, y, si}) for a given image, suitable
   * for canvas drawing and image export. Uses the same contour ID
   * resolution as `getImageSegmentationGeometry`.
   *
   * Returns named mask exports (MyocardiumMask, LvBloodpoolMask,
   * RvBloodpoolMask, InsertionPointMask) with proper viewport offsets.
   */
  function getImageMaskPixels(
    imageUID: string,
    orientation: Orientation,
    viewerID: string,
  ): ContourExport[] {
    const exports: ContourExport[] = [];

    const container = contourContainerStore.contourContainers[imageUID];
    if (!container) return exports;

    const { myoContourId, endoContourId } = getContourIdsForOrientation(
      orientation,
      viewerID,
    );

    const isLax = isLaxOrientation(orientation);

    // ── Myocardium mask ──
    const myoMaskPixels = container.getContourMaskCompleteIn(myoContourId);
    if (myoMaskPixels.length > 0) {
      // Subtract endocardial (blood pool) pixels from the myocardium mask
      const endoPixels = container.getContourMaskCompleteIn(endoContourId);
      let myoPoints: ExportPoint[] = [...myoMaskPixels];

      if (endoPixels.length > 0) {
        const subtractSet = new Set(endoPixels.map(p => `${p.x},${p.y}`));
        myoPoints = myoPoints.filter(p => !subtractSet.has(`${p.x},${p.y}`));
      }

      exports.push({ name: 'MyocardiumMask', points: myoPoints });
    }

    // ── LV bloodpool mask ──
    const endoPixels = container.getContourMaskCompleteIn(endoContourId);
    if (endoPixels.length > 0) {
      exports.push({ name: 'LvBloodpoolMask', points: [...endoPixels] });
    }

    // ── RV bloodpool mask (SAX only) ──
    if (!isLax) {
      const rvEndoContours = contourContainerStore.getContourOfLabel(
        imageUID,
        ContourLabel.SaxRvEndo,
      );
      if (rvEndoContours.length > 0) {
        const rvContourId: ContourId = {
          label: ContourLabel.SaxRvEndo,
          id: encodeContourLabelItemId([0]),
        };
        const rvPixels = container.getContourMaskCompleteIn(rvContourId);
        if (rvPixels.length > 0) {
          exports.push({ name: 'RvBloodpoolMask', points: [...rvPixels] });
        }
      }
    }

    // ── Insertion point ──
    const insertionPointLabel = isLax
      ? ContourLabel.LaxLvEndoBasalPoint
      : ContourLabel.RvInsertion;

    const insertionContours = contourContainerStore.getContourOfLabel(
      imageUID,
      insertionPointLabel,
    );
    if (insertionContours.length > 0) {
      const insertionContourId: ContourId = {
        label: insertionPointLabel,
        id: encodeContourLabelItemId([0]),
      };
      const contour = container.getContour(insertionContourId);
      if (contour) {
        const polygon = contour.getPolygonScaled();
        if (polygon.length > 0) {
          const points: ExportPoint[] = polygon.map(p => ({
            x: p.x,
            y: p.y,
            si: 0,
          }));
          exports.push({ name: 'InsertionPointMask', points });
        }
      }
    }

    return exports;
  }

  // ─── Orientation Detection ────────────────────────────────────────────
  /**
   * Auto-detects orientation (SAX vs LAX) for a given image by probing
   * which contour labels are present. This is useful when the caller
   * doesn't have orientation context (e.g. the image extractor).
   *
   * Returns Orientation.Sax if SAX contours are found,
   * Orientation.Lax2CV if LAX contours are found (generic LAX),
   * or Orientation.Sax as the default fallback.
   */
  function detectImageOrientation(imageUID: string): Orientation {
    const hasSaxContours
      = contourContainerStore.getContourOfLabel(imageUID, ContourLabel.SaxRvEndo)
        .length > 0
        || contourContainerStore.getContourOfLabel(imageUID, ContourLabel.SaxLvEndo)
          .length > 0
          || contourContainerStore.getContourOfLabel(imageUID, ContourLabel.SaxLvEpi)
            .length > 0
            || contourContainerStore.getContourOfLabel(
              imageUID,
              ContourLabel.SaxLvMyoFusion,
            ).length > 0;

    if (hasSaxContours) return Orientation.Sax;

    const hasLaxContours
      = contourContainerStore.getContourOfLabel(imageUID, ContourLabel.LaxLvEndo)
        .length > 0
        || contourContainerStore.getContourOfLabel(imageUID, ContourLabel.LaxLvEpi)
          .length > 0
          || contourContainerStore.getContourOfLabel(
            imageUID,
            ContourLabel.LaxLvMyoFusion,
          ).length > 0;

    if (hasLaxContours) return Orientation.Lax2CV;

    // Default to SAX if no contours found
    return Orientation.Sax;
  }

  return {
    getContourIdsForOrientation,
    getImageSegmentationGeometry,
    getImageMaskPixels,
    detectImageOrientation,
  };
}
